import xmlFormat from 'xml-formatter';

import {
  BaseField,
  DocumentDefinition,
  DocumentDefinitionType,
  DocumentType,
  IDocument,
  IField,
  IParentType,
  PrimitiveDocument,
} from '../../models/datamapper/document';
import {
  FieldItem,
  InstructionItem,
  MappingItem,
  MappingParentType,
  MappingTree,
  OtherwiseItem,
  UnknownMappingItem,
  ValueOfSelector,
  ValueOfType,
  VariableItem,
} from '../../models/datamapper/mapping';
import { DeserializeItemResult, DeserializeResult, MappingItemClass } from '../../models/datamapper/serialization';
import { NS_XSL } from '../../models/datamapper/standard-namespaces';
import { DEFAULT_DATAMAPPER_SETTINGS, FieldOverrideVariant, IDataMapperSettings } from '../../models/datamapper/types';
import { SendAlertProps } from '../../models/datamapper/visualization';
import { DocumentUtilService } from '../document/document-util.service';
import { FieldOverrideService } from '../document/field-override.service';
import { XmlSchemaDocumentUtilService } from '../document/xml-schema/xml-schema-document-util.service';
import { MappingService } from './mapping.service';
import { MappingSerializerJsonAddon } from './mapping-serializer-json-addon';
import { deserializeHandlers, serializeHandlers } from './xslt-item-handlers';

export const DO_NOT_EDIT_COMMENT = '<!-- This file is generated by Kaoto DataMapper. Do not edit. -->';

export function getEmptyXsl(dataMapperSettings?: IDataMapperSettings): string {
  const xslOutputAttrs = [
    `method="xml"`,
    `indent="yes"`,
    dataMapperSettings?.omitXmlDeclaration && `omit-xml-declaration="yes"`,
  ]
    .filter(Boolean)
    .join(' ');

  return `<?xml version="1.0" encoding="UTF-8"?>
${DO_NOT_EDIT_COMMENT}
<xsl:stylesheet version="3.0" xmlns:xsl="${NS_XSL}">
  <xsl:output ${xslOutputAttrs}/>
</xsl:stylesheet>
`;
}

export const EMPTY_XSL = getEmptyXsl();

export class MappingSerializerService {
  static createNew(dataMapperSettings?: IDataMapperSettings) {
    return new DOMParser().parseFromString(getEmptyXsl(dataMapperSettings), 'application/xml');
  }

  private static sortFields(left: IField, right: IField) {
    return left.parent.fields.indexOf(left) - right.parent.fields.indexOf(right);
  }

  private static getMappingItemFields(item: MappingItem): IField[] {
    if (item instanceof FieldItem) return [item.field];
    if (item instanceof InstructionItem) return MappingService.getInstructionFields(item);
    return [];
  }

  private static hasAttributeField(fields: IField[]): boolean {
    return fields.some((f) => f.isAttribute);
  }

  private static compareSpecialMappingItems(left: MappingItem, right: MappingItem): number | null {
    if (left instanceof VariableItem) return right instanceof VariableItem ? 0 : -1;
    if (right instanceof VariableItem) return 1;
    if (left instanceof UnknownMappingItem || right instanceof UnknownMappingItem) return 0;
    if (left instanceof OtherwiseItem) return right instanceof OtherwiseItem ? 0 : 1;
    if (right instanceof OtherwiseItem) return -1;
    if (left instanceof InstructionItem && right instanceof InstructionItem) return 0;
    return null;
  }

  private static compareMappingItemFields(leftFields: IField[], rightFields: IField[]) {
    if (leftFields.length === 0) return rightFields.length === 0 ? 0 : 1;
    if (rightFields.length === 0) return -1;

    const leftHasAttribute = MappingSerializerService.hasAttributeField(leftFields);
    const rightHasAttribute = MappingSerializerService.hasAttributeField(rightFields);

    if (leftHasAttribute !== rightHasAttribute) return leftHasAttribute ? -1 : 1;
    if (leftHasAttribute && rightHasAttribute) return 0;

    const leftFirst = [...leftFields].sort(MappingSerializerService.sortFields)[0];
    const rightFirst = [...rightFields].sort(MappingSerializerService.sortFields)[0];
    return leftFirst.parent.fields.indexOf(leftFirst) - rightFirst.parent.fields.indexOf(rightFirst);
  }

  private static sortMappingItem(left: MappingItem, right: MappingItem) {
    const specialComparison = MappingSerializerService.compareSpecialMappingItems(left, right);
    if (specialComparison !== null) return specialComparison;

    const leftFields = MappingSerializerService.getMappingItemFields(left);
    const rightFields = MappingSerializerService.getMappingItemFields(right);

    return MappingSerializerService.compareMappingItemFields(leftFields, rightFields);
  }

  /**
   * Serialize the {@link MappingTree} model object into an XSLT mappings document string.
   * @param mappings {@link MappingTree} object to write
   * @param sourceParameterMap source paramter map
   * @param dataMapperSettings DataMapper settings
   */
  static serialize(
    mappings: MappingTree,
    sourceParameterMap: Map<string, IDocument>,
    dataMapperSettings?: IDataMapperSettings,
  ): string {
    const xslt = MappingSerializerService.createNew(dataMapperSettings);
    MappingSerializerService.populateNamespaces(xslt, mappings.namespaceMap);
    MappingSerializerService.populateParam(xslt, sourceParameterMap);
    const root = MappingSerializerService.populateMappingRoot(xslt, mappings);
    mappings.children.sort(MappingSerializerService.sortMappingItem).forEach((mapping) => {
      MappingSerializerService.populateMapping(root, mapping);
    });
    return xmlFormat(new XMLSerializer().serializeToString(xslt));
  }

  private static populateNamespaces(xslt: Document, namespaceMap: { [prefix: string]: string }) {
    const rootElement = xslt.documentElement;
    const prefixes: string[] = [];
    Object.entries(namespaceMap).forEach(([prefix, uri]) => {
      if (prefix && uri) {
        rootElement.setAttribute(`xmlns:${prefix}`, uri);
        prefixes.push(prefix);
      }
    });
    // These prefixes are only declared for matching the source document via XPath.
    // Without `exclude-result-prefixes` they leak into the output as redundant
    // namespace declarations, which some consumers (e.g. HAPI HL7 v2 XMLParser)
    // fail to parse. `exclude-result-prefixes` only suppresses declarations that
    // are not actually used by an output element, so this is safe.
    if (prefixes.length > 0) {
      rootElement.setAttribute('exclude-result-prefixes', prefixes.join(' '));
    }
  }

  private static getRootStyleSheet(xsltDocument: Document) {
    const prefix = xsltDocument.lookupPrefix(NS_XSL);
    const nsResolver = xsltDocument.createNSResolver(xsltDocument);
    return xsltDocument
      .evaluate(`/${prefix}:stylesheet`, xsltDocument, nsResolver, XPathResult.ANY_TYPE)
      .iterateNext()! as Element;
  }

  private static populateMappingRoot(xsltDocument: Document, mappings: MappingTree) {
    const template = xsltDocument.createElementNS(NS_XSL, 'template');
    template.setAttribute('match', '/');
    const stylesheet = MappingSerializerService.getRootStyleSheet(xsltDocument);
    stylesheet.appendChild(template);

    const jsonRoot = MappingSerializerJsonAddon.populateJsonTargetBase(mappings, template);
    if (jsonRoot) return jsonRoot;

    return template;
  }

  private static populateParam(xsltDocument: Document, sourceParameterMap: Map<string, IDocument>) {
    const stylesheet = MappingSerializerService.getRootStyleSheet(xsltDocument);
    sourceParameterMap.forEach((doc, paramName) => {
      const prefix = xsltDocument.lookupPrefix(NS_XSL);
      const nsResolver = xsltDocument.createNSResolver(xsltDocument);
      const existing = xsltDocument
        .evaluate(
          `/${prefix}:stylesheet/${prefix}:param[@name='${paramName}']`,
          xsltDocument,
          nsResolver,
          XPathResult.ANY_TYPE,
        )
        .iterateNext();
      if (existing) return;

      const xsltParam = xsltDocument.createElementNS(NS_XSL, 'param');
      xsltParam.setAttribute('name', paramName);
      stylesheet.appendChild(xsltParam);

      MappingSerializerJsonAddon.populateJsonToXmlVariable(doc, stylesheet, paramName);
    });
  }

  private static populateMapping(parent: Element, mapping: MappingItem) {
    // Add comment before the element if it exists
    if (mapping.comment) {
      const xsltDocument = parent.ownerDocument;
      const comment = xsltDocument.createComment(` ${mapping.comment} `);
      const sanitized = comment.data.replace(/--/g, '- -');
      comment.data = sanitized;
      parent.appendChild(comment);
    }

    const handler = serializeHandlers.get(mapping.constructor as MappingItemClass);
    const child = handler?.serialize(parent, mapping) ?? null;
    if (!child) {
      return;
    }

    mapping.children.sort(MappingSerializerService.sortMappingItem);
    for (const childItem of mapping.children) {
      MappingSerializerService.populateMapping(child, childItem);
    }
  }

  /**
   * Deserialize the XSLT mappings document into a {@link MappingTree} model object.
   * @param xslt XSLT mappings document in string format
   * @param targetDocument Target Document
   * @param mappingTree {@link MappingTree} object to write
   * @param sourceParameterMap source parameter map
   */
  static deserialize(
    xslt: string,
    targetDocument: IDocument,
    mappingTree: MappingTree,
    sourceParameterMap: Map<string, IDocument>,
  ): DeserializeResult {
    mappingTree.children = [];
    const messages: SendAlertProps[] = [];
    const xsltDoc = new DOMParser().parseFromString(xslt, 'application/xml');
    const dataMapperSettings = MappingSerializerService.restoreDataMapperSettings(xsltDoc);
    const template = xsltDoc.getElementsByTagNameNS(NS_XSL, 'template')[0];
    if (!template?.children) return { mappingTree, messages, dataMapperSettings };
    MappingSerializerService.restoreNamespaces(xsltDoc, mappingTree);
    MappingSerializerService.restoreParam(xsltDoc, sourceParameterMap);
    const root = MappingSerializerJsonAddon.getJsonTargetBase(xsltDoc, mappingTree) ?? template;
    for (const item of Array.from(root.childNodes)) {
      MappingSerializerService.restoreMapping(item, targetDocument, mappingTree, messages);
    }
    return { mappingTree, messages, dataMapperSettings };
  }

  private static restoreDataMapperSettings(xsltDocument: Document): IDataMapperSettings {
    const output = xsltDocument.getElementsByTagNameNS(NS_XSL, 'output')[0];

    return {
      ...DEFAULT_DATAMAPPER_SETTINGS,
      omitXmlDeclaration: output?.getAttribute('omit-xml-declaration') === 'yes',
    };
  }

  private static restoreNamespaces(xsltDocument: Document, mappingTree: MappingTree) {
    mappingTree.namespaceMap = {};
    const rootElement = xsltDocument.documentElement;
    Array.from(rootElement.attributes).forEach((attr) => {
      if (!attr.nodeName.includes(':') || !attr.nodeValue || attr.nodeValue === NS_XSL) return;
      const splitted = attr.nodeName.split(':');
      if (splitted[0] === 'xmlns') mappingTree.namespaceMap[splitted[1]] = attr.nodeValue;
    });
  }

  private static restoreParam(xsltDocument: Document, sourceParameterMap: Map<string, IDocument>) {
    const prefix = xsltDocument.lookupPrefix(NS_XSL);
    const nsResolver = xsltDocument.createNSResolver(xsltDocument);
    const params = xsltDocument.evaluate(
      `/${prefix}:stylesheet/${prefix}:param`,
      xsltDocument,
      nsResolver,
      XPathResult.ANY_TYPE,
    );
    let param: Node | null;
    while ((param = params.iterateNext())) {
      const paramEl = param as Element;
      const name = paramEl.getAttribute('name');
      if (!name || sourceParameterMap.has(name)) continue;
      sourceParameterMap.set(
        name,
        new PrimitiveDocument(new DocumentDefinition(DocumentType.PARAM, DocumentDefinitionType.Primitive, name)),
      );
    }
  }

  private static restoreMapping(
    item: Node,
    parentField: IParentType,
    parentMapping: MappingParentType,
    messages: SendAlertProps[],
  ) {
    if (item.nodeType === Node.TEXT_NODE) {
      MappingSerializerService.restoreTextNode(item, parentMapping);
    } else if (item.nodeType === Node.ELEMENT_NODE) {
      MappingSerializerService.restoreElementNode(item as Element, parentField, parentMapping, messages);
    }
  }

  private static restoreTextNode(item: Node, parentMapping: MappingParentType) {
    const literal = (item.textContent || '').trim();
    if (!literal) return;
    const selector = new ValueOfSelector(parentMapping, ValueOfType.VALUE);
    selector.expression = literal;
    selector.isLiteral = true;
    parentMapping.children.push(selector);
  }

  private static restoreCommentFromPreviousSibling(element: Element, mappingItem: MappingItem) {
    // Look for preceding comment node
    let previousSibling = element.previousSibling;
    while (previousSibling?.nodeType === Node.TEXT_NODE) {
      previousSibling = previousSibling.previousSibling;
    }
    if (previousSibling?.nodeType === Node.COMMENT_NODE) {
      mappingItem.comment = (previousSibling as Comment).data.trim();
    }
  }

  private static isUserCreatedField(field: IField): boolean {
    if (field.typeOverride === FieldOverrideVariant.SUBSTITUTION) return true;

    let current: IParentType = field.parent;
    while ('ownerDocument' in current) {
      const f = current;
      if (f.wrapperKind === 'abstract' && f.selectedMemberQName !== undefined) return true;
      if (f.wrapperKind === 'choice' && f.selectedMemberIndex !== undefined) return true;
      if (f.wrapperKind) {
        current = f.parent;
        continue;
      }
      break;
    }

    return false;
  }

  private static restoreElementNode(
    element: Element,
    parentField: IParentType,
    parentMapping: MappingParentType,
    messages: SendAlertProps[],
  ) {
    const result =
      element.namespaceURI === NS_XSL
        ? MappingSerializerService.restoreXslElement(element, parentField, parentMapping, messages)
        : MappingSerializerService.restoreNonXslElement(element, parentField, parentMapping);

    if (!result?.mappingItem) return;

    if (result.mappingItem instanceof FieldItem) {
      const hasContent = Array.from(element.childNodes).some(
        (node) => node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()),
      );
      if (!hasContent || MappingSerializerService.isUserCreatedField(result.mappingItem.field)) {
        result.mappingItem.isUserCreated = true;
      }
    }

    parentMapping.children.push(result.mappingItem);

    const isXslText = element.namespaceURI === NS_XSL && element.localName === 'text';
    if (!(result.mappingItem instanceof UnknownMappingItem) && !isXslText) {
      for (const childItem of Array.from(element.childNodes)) {
        MappingSerializerService.restoreMapping(
          childItem,
          result.fieldItem ?? parentField,
          result.mappingItem,
          messages,
        );
      }
    }
  }

  private static restoreNonXslElement(
    element: Element,
    parentField: IParentType,
    parentMapping: MappingParentType,
  ): { mappingItem: MappingItem; fieldItem: IParentType | null } | null {
    if (parentField instanceof PrimitiveDocument) return null;
    const field = MappingSerializerService.getOrCreateElementField(element, parentField);
    if (!field) return null;
    const mappingItem = new FieldItem(parentMapping, field);
    MappingSerializerService.restoreCommentFromPreviousSibling(element, mappingItem);
    return { fieldItem: field, mappingItem };
  }

  private static restoreXslElement(
    element: Element,
    parentField: IParentType,
    parentMapping: MappingParentType,
    messages: SendAlertProps[],
  ): DeserializeItemResult<MappingItem> | null {
    const handler = deserializeHandlers.get(element.localName);
    let result: DeserializeItemResult<MappingItem> | null;
    if (handler) {
      result = handler.deserialize(element, parentField, parentMapping) as DeserializeItemResult<MappingItem> | null;
    } else {
      result = {
        mappingItem: new UnknownMappingItem(parentMapping, element),
        fieldItem: null,
        messages: [
          {
            variant: 'warning',
            title: `Unrecognized XSLT element: "xsl:${element.localName}"`,
            description: new XMLSerializer().serializeToString(element),
          },
        ],
      };
    }
    if (!result) return null;
    if (result.messages) {
      messages.push(...result.messages);
    }
    if (result.mappingItem) {
      MappingSerializerService.restoreCommentFromPreviousSibling(element, result.mappingItem);
    }

    return result;
  }

  private static getOrCreateElementField(item: Element, parentField: IParentType): IField {
    const jsonField = MappingSerializerJsonAddon.getOrCreateJsonField(item, parentField);
    if (jsonField) return jsonField;

    const namespace = item.namespaceURI || '';
    const name = item.localName;
    const existing = XmlSchemaDocumentUtilService.getChildField(parentField, name, namespace);
    if (existing) return existing;

    const substitutionMatch = FieldOverrideService.findSubstitutionGroupHead(parentField, name, namespace);
    if (substitutionMatch?.headField.maxOccurs === 1) {
      DocumentUtilService.applySubstitutionToField(substitutionMatch.headField, substitutionMatch.info);
      return substitutionMatch.headField;
    }

    const field = new BaseField(
      parentField,
      'ownerDocument' in parentField ? parentField.ownerDocument : parentField,
      name,
    );
    field.namespaceURI = namespace;
    parentField.fields.push(field);
    return field;
  }
}
